import React, { useEffect, useState } from 'react';
import ChessboardComponent from './ChessboardComponent';
import GameControls from './GameControls';
import MobileTimer from './MobileTimer';
import useGameStore from '../store/gameStore';
import { motion, AnimatePresence } from 'framer-motion';
import { getModelDisplayName } from '../constants/modelConstants';

interface ChessGameProps {
  selectedModel: string;
  useVision: boolean;
  onModelChange: (model: string) => void;
  onVisionChange: (useVision: boolean) => void;
  recordGame?: () => boolean;
}

const ChessGame: React.FC<ChessGameProps> = ({ 
  selectedModel, 
  useVision,
  onModelChange,
  onVisionChange,
  recordGame
}) => {
  const [showMobileSetup, setShowMobileSetup] = useState(false);
  
  const {
    game,
    isThinking,
    lastMove,
    boardOrientation,
    isGameActive,
    playerColor,
    gameTimeLimit,
    playerTimeRemaining,
    modelTimeRemaining,
    timeOut,
    isGameOver,
    activePlayer,
    setSelectedModel,
    setUseVision,
    setPlayerColor,
    setGameTimeLimit,
    resetGame,
    makeMove,
    decrementTimer
  } = useGameStore();
  
  // Set initial model and vision from props
  useEffect(() => {
    setSelectedModel(selectedModel);
  }, [selectedModel, setSelectedModel]);
  
  useEffect(() => {
    setUseVision(useVision);
  }, [useVision, setUseVision]);
  
  // Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    const gameIsOver = isGameOver();
    if (isGameActive && !gameIsOver) {
        timer = setInterval(() => {
          decrementTimer();
        }, 100);
      }
      
      return () => clearInterval(timer);
    }, [isGameActive, isGameOver, decrementTimer, game.fen()]);
  
  // Handle model change
  const handleModelChange = (model: string) => {
    setSelectedModel(model);
    onModelChange(model);
  };
  
  // Handle model with vision change
  const handleModelWithVisionChange = (model: string, visionEnabled: boolean) => {
    setSelectedModel(model);
    setUseVision(visionEnabled);
    onModelChange(model);
    onVisionChange(visionEnabled);
  };

  // Handle game time limit change
  const handleGameTimeLimitChange = (minutes: 3 | 5 | 10) => {
    setGameTimeLimit(minutes);
  };

  // Handle game reset with game limit check
  const handleResetGame = (startNewGame?: boolean) => {
    // Use a default value of false if startNewGame is undefined
    const shouldStartNewGame = startNewGame ?? false;
    
    // Only check game limit when starting a new game
    if (shouldStartNewGame && recordGame) {
      // If recordGame returns false, don't start a new game
      if (!recordGame()) {
        return;
      }
    }
    
    // Proceed with resetting the game
    resetGame(shouldStartNewGame);
  };

  return (
    <div className="relative flex flex-col items-center gap-8 lg:flex-row lg:justify-center">
      {/* Mobile Timer - visibile solo su mobile quando il gioco è attivo */}
      {isGameActive && !isGameOver() && (
        <div className="w-full mb-2 lg:hidden">
          <MobileTimer
            modelName={getModelDisplayName(selectedModel)}
            modelTimeRemaining={modelTimeRemaining}
            playerTimeRemaining={playerTimeRemaining}
            activePlayer={activePlayer}
            onResign={() => resetGame(false)}
          />
        </div>
      )}
      
      {/* Chessboard container */}
      <div className="relative w-full max-w-[500px]">
        <ChessboardComponent 
          position={game.fen()}
          onPieceDrop={(from, to) => makeMove(from, to)}
          lastMove={lastMove}
          boardOrientation={boardOrientation}
          isThinking={isThinking}
          isGameActive={isGameActive}
        />

        {/* Game Controls overlay on mobile */}
        <AnimatePresence>
          {/* Always show the GameOver screen when the game is over */}
          {(isGameOver() || (!isGameActive && showMobileSetup)) && (
            <motion.div 
              className="absolute inset-0 flex items-center justify-center lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute inset-0 bg-black/70"></div>
              <div className="relative z-10 w-[90%] max-w-[350px]">
                <GameControls 
                  selectedModel={selectedModel}
                  useVision={useVision}
                  playerColor={playerColor}
                  gameTimeLimit={gameTimeLimit}
                  isGameOver={isGameOver()}
                  isCheckmate={game.isCheckmate()}
                  isDraw={game.isDraw()}
                  isGameActive={isGameActive}
                  game={game}
                  boardOrientation={boardOrientation}
                  playerTimeRemaining={playerTimeRemaining}
                  modelTimeRemaining={modelTimeRemaining}
                  timeOut={timeOut}
                  activePlayer={activePlayer}
                  onModelChange={handleModelChange}
                  onModelWithVisionChange={handleModelWithVisionChange}
                  onPlayerColorChange={setPlayerColor}
                  onGameTimeLimitChange={handleGameTimeLimitChange}
                  onResetGame={handleResetGame}
                  onCloseSetup={() => setShowMobileSetup(false)}
                  isMobile={true}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Mobile New Game button - only show when no game is active and not showing the setup screen */}
      {(!isGameActive && !isGameOver() && !showMobileSetup) && (
        <motion.div 
          className="lg:hidden w-full max-w-[500px] mt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <button
            className="w-full rounded bg-bg-dark text-terminal-green border border-terminal-green p-3 font-vt323 text-xl cursor-pointer hover:bg-dark-green"
            onClick={() => setShowMobileSetup(true)}
          >
            <span className="font-vt323 text-2xl animate-pulse">$ PRESS START</span>
          </button>
        </motion.div>
      )}
      
      {/* Desktop Game Controls - visibili solo su desktop */}
      <div className="hidden lg:block w-[360px]">
        <GameControls 
          selectedModel={selectedModel}
          useVision={useVision}
          playerColor={playerColor}
          gameTimeLimit={gameTimeLimit}
          isGameOver={isGameOver()}
          isCheckmate={game.isCheckmate()}
          isDraw={game.isDraw()}
          isGameActive={isGameActive}
          game={game}
          boardOrientation={boardOrientation}
          playerTimeRemaining={playerTimeRemaining}
          modelTimeRemaining={modelTimeRemaining}
          timeOut={timeOut}
          activePlayer={activePlayer}
          onModelChange={handleModelChange}
          onModelWithVisionChange={handleModelWithVisionChange}
          onPlayerColorChange={setPlayerColor}
          onGameTimeLimitChange={handleGameTimeLimitChange}
          onResetGame={handleResetGame}
          isMobile={false}
        />
      </div>
    </div>
  );
};

export default ChessGame;